import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Header";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  CircularProgress,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import apis from "../utils/apis";
import { useSelector, useDispatch } from "react-redux";
import { setResumeCarousel } from "../redux/action/result";
import { setUserStatus } from "../redux/action/user";
import { setLoading } from "../redux/action/loadingRequest";
import { getEmail } from "../utils/storage";
import { useNavigate } from "react-router-dom";

// Define extractFileName at the top level
const extractFileName = (fileUrl) => {
  if (!fileUrl) return "";
  const lastSlash = fileUrl.lastIndexOf("/");
  let fileNameWithExt = fileUrl.substring(lastSlash + 1);
  fileNameWithExt = fileNameWithExt.split("?")[0];
  const extMatch = fileNameWithExt.match(/\.(pdf|docx|txt)/i);
  if (extMatch) {
    const extIdx = fileNameWithExt.indexOf(extMatch[0]);
    return decodeURIComponent(
      fileNameWithExt.substring(0, extIdx + extMatch[0].length)
    );
  }
  const lastDot = fileNameWithExt.lastIndexOf(".");
  if (lastDot === -1) return fileNameWithExt;
  return decodeURIComponent(fileNameWithExt.substring(0, lastDot));
};

const formatISTDateTime = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const PendingResumes = ({
  secretEncodeDecodeString,
  staticRandomEncodeDecodeString,
}) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [loading, setLoadingState] = useState(true);
  const [error, setError] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState({
    fileName: "asc",
    createdAt: "asc",
    status: "asc",
  });
  const userRefId = useSelector((state) => state.userReducer.userRefId);
  const userEmail = getEmail();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cache = useRef({});

  // Fetch data with pagination
  const fetchData = async (currentPage, limit, isTableLoading = false) => {
    const apiPage = currentPage + 1;
    if (cache.current[apiPage]) {
      setPendingFiles(cache.current[apiPage].data);
      setTotalCount(cache.current[apiPage].totalCount);
      return;
    }

    if (isTableLoading) setTableLoading(true);
    else setLoadingState(true);

    try {
      const response = await apis.GET({
        endpoint: `/get-uploaded-and-processing-files?page=${apiPage}&limit=${limit}`,
      });
      console.log(
        `API Response for page ${apiPage}, limit ${limit}:`,
        response.data
      );

      const files = response.data.files.map((f) => ({
        ...f,
        fileName: f.fileName || extractFileName(f.fileUrl),
      }));
      console.log(`Processed files (length: ${files.length}):`, files);

      // Ensure files length matches limit (except possibly last page)
      if (files.length > limit) {
        console.warn(`API returned ${files.length} items, expected ${limit}`);
        files.length = limit; // Truncate to expected size
      }

      const totalCount = response.data.total;
      setPendingFiles(files);
      console.log(`Set pendingFiles for page ${currentPage}:`, files);
      setTotalCount(totalCount);
      cache.current[apiPage] = { data: files, totalCount };
    } catch (err) {
      if (isTableLoading) setTableError("Failed to load table data.");
      else setError("Failed to fetch pending resumes.");
    } finally {
      if (isTableLoading) setTableLoading(false);
      else setLoadingState(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, []);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage, rowsPerPage, true);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    cache.current = {};
    fetchData(0, newRowsPerPage, true);
  };

  // Encryption function
  function DataEncryption(json) {
    const jsonString = JSON.stringify(json);
    const customStringJson =
      secretEncodeDecodeString + jsonString + secretEncodeDecodeString;
    const newCustomStringJson = customStringJson.replace(
      /"/g,
      staticRandomEncodeDecodeString
    );
    const encodedString = btoa(newCustomStringJson);
    return encodedString;
  }

  // Decryption function
  function DataDecryption(jsonString) {
    try {
      const decodedString = atob(jsonString);
      const getOldStringOne = decodedString.replace(
        new RegExp(secretEncodeDecodeString, "g"),
        ""
      );
      const str = staticRandomEncodeDecodeString;
      const escapedString = str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(escapedString, "g");
      const getOldStringTwo = getOldStringOne.replace(regex, '"');
      return JSON.parse(getOldStringTwo);
    } catch (error) {
      throw new Error("Decryption failed. Invalid response structure.");
    }
  }

  // Add fetchSkillValidity function (copy from ResumeUploadRecruiter.js)
  const fetchSkillValidity = async (skill) => {
    try {
      const encryptedPayload = DataEncryption({ primarySkill: skill });
      const response = await apis.POST({
        endpoint: "/get-topSkillValidity",
        payload: encryptedPayload,
        options: { type: "json" },
      });
      if (!response.data)
        throw new Error("No data received from skill validity API.");
      const decryptedData = DataDecryption(response.data);
      return decryptedData;
    } catch (error) {
      throw new Error(
        "We couldn't validate your skills due to a server issue. Please try again later."
      );
    }
  };

  // Process resume flow
  const handleProceed = async (file) => {
    setLoadingFiles((prev) => ({ ...prev, [file.fileUrl]: true }));
    setError(null);
    try {
      const processResponse = await apis.POST({
        endpoint: "/process-resumes",
        payload: { fileName: file.fileName, fileUrl: file.fileUrl },
        options: { type: "json" },
      });
      if (
        !processResponse.data ||
        !processResponse.data.processedResume?.extractedText
      ) {
        throw new Error("Failed to process the resume.");
      }
      let pdfText = processResponse.data.processedResume.extractedText;

      const parsedResponse = await apis.POST({
        endpoint: "/get-parsed-resume",
        payload: { pdfText },
        options: { type: "json" },
      });
      if (!parsedResponse.data) throw new Error("Failed to parse the resume.");
      let parsedResumeData = JSON.parse(parsedResponse.data.gptResponse);
      parsedResumeData.pdfText = pdfText;

      const updatedFormResponse = await apis.POST({
        endpoint: "/get-updated-form",
        payload: parsedResumeData,
        options: { type: "json" },
      });
      if (!updatedFormResponse.data)
        throw new Error("Failed to fetch updated form data.");

      const processSkills = async (skillType, skillData) => {
        if (Array.isArray(skillData) && skillData.length > 0) {
          try {
            const validatedSkill = await fetchSkillValidity(skillData);
            if (validatedSkill && validatedSkill.topPrimarySkill) {
              updatedFormResponse.data.userForm[skillType] =
                validatedSkill.topPrimarySkill;
            } else {
              console.warn(`Invalid response for ${skillType} validity.`);
            }
          } catch (error) {
            console.error(
              `Error fetching ${skillType} validity:`,
              error.message
            );
          }
        } else {
          console.warn(`${skillType} is not a valid array or is empty.`);
        }
      };
      console.log(updatedFormResponse.data, "updatedFormResponse.data");
      await processSkills(
        "primarySkill",
        updatedFormResponse.data.userForm.primarySkill
      );
      await processSkills(
        "secondarySkill",
        updatedFormResponse.data.userForm.secondarySkill
      );

      const resultPayload = {
        ...updatedFormResponse.data.userForm,
        userRefId: userRefId,
        recruiterEmail: userEmail,
        uploadedByRecruiter: true,
        fileName: file.fileName,
        fileUrl: file.fileUrl,
        payloadType: "carousel-resume",
      };
      const resultResponse = await apis.POST({
        endpoint: "/get-result",
        payload: resultPayload,
        options: { type: "json" },
      });
      if (!resultResponse.data) throw new Error("Failed to fetch result data.");

      setPendingFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.fileUrl === file.fileUrl
            ? { ...f, status: "Completed", uuid: resultResponse.data.uuid }
            : f
        )
      );
    } catch (err) {
      setError(
        err?.message || "Failed to process the resume. Please try again."
      );
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [file.fileUrl]: false }));
    }
  };

  // Add this function to call the delete API
  const deletePendingFileStatus = async ({ id, limit, page }) => {
    try {
      const response = await apis.DELETE({
        endpoint: `/delete-pending-file-status?id=${encodeURIComponent(
          id
        )}&limit=${limit}&page=${page}`,
      });
      return response;
    } catch (err) {
      // Optionally handle error
      return null;
    }
  };

  // Handler for delete button (open dialog)
  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    setDeleteLoading(true);
    const response = await deletePendingFileStatus({
      id: fileToDelete._id || fileToDelete.id,
      limit: rowsPerPage,
      page: page + 1,
    });
    cache.current = {};
    if (response && response.data && Array.isArray(response.data.files)) {
      const files = response.data.files.map((f) => ({
        ...f,
        fileName: f.fileName || extractFileName(f.fileUrl),
      }));
      let totalCount = response.data.total;
      if (typeof totalCount !== "number") {
        if (
          typeof response.data.totalPages === "number" &&
          typeof response.data.limit === "number"
        ) {
          totalCount = response.data.totalPages * response.data.limit;
        } else {
          totalCount = files.length;
        }
      }
      setPendingFiles(files);
      setTotalCount(totalCount);
      cache.current[page + 1] = {
        data: files,
        totalCount: totalCount,
      };
      if (files.length === 0 && totalCount > 0 && page > 0) {
        setPage(page - 1);
      }
    } else {
      fetchData(page, rowsPerPage, true);
    }
    setDeleteLoading(false);
    setDeleteDialogOpen(false);
    setFileToDelete(null);
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setFileToDelete(null);
  };

  // Sorting handler for columns
  const handleSort = (column) => {
    setSortOrder((prev) => {
      const newOrder = prev[column] === "asc" ? "desc" : "asc";
      // Reset other columns' sortOrder to 'asc'
      return {
        fileName: column === "fileName" ? newOrder : "asc",
        createdAt: column === "createdAt" ? newOrder : "asc",
        status: column === "status" ? newOrder : "asc",
      };
    });

    setPendingFiles((prevFiles) => {
      const sorted = [...prevFiles].sort((a, b) => {
        let aValue, bValue;
        if (column === "fileName") {
          aValue = (a.fileName || "").toLowerCase();
          bValue = (b.fileName || "").toLowerCase();
        } else if (column === "createdAt") {
          aValue = a.createdAt ? new Date(a.createdAt) : new Date(0);
          bValue = b.createdAt ? new Date(b.createdAt) : new Date(0);
        } else if (column === "status") {
          aValue = (a.status || "").toLowerCase();
          bValue = (b.status || "").toLowerCase();
        }
        if (aValue < bValue) return sortOrder[column] === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder[column] === "asc" ? 1 : -1;
        return 0;
      });
      return sorted;
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        minHeight: "100vh",
        width: "100%",
        color: "#FDF9E0",
        backgroundImage: "url('../../assets/images/homepage-background.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: 4,
      }}
    >
      <Header />
      <Typography
        variant="h4"
        sx={{ mt: 10, fontWeight: 700, fontFamily: "Kanit" }}
      >
        Pending Resumes
      </Typography>
      <Typography sx={{ mt: 2, mb: 4 }}>
        Below are resumes that are uploaded and pending processing.
      </Typography>
      {loading ? (
        <CircularProgress sx={{ color: "#FDF9E0" }} />
      ) : (
        <>
          <TableContainer
            sx={{
              border: "1px solid #FDF9E0",
              borderRadius: "8px",
              maxWidth: { xs: "100%", md: "100%", xl: "70%" },
              overflowX: "auto",
              margin: "none !important",
              background: "transparent",
            }}
            component={Paper}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#FDF9E0", fontWeight: "bold" }}>
                    No
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#FDF9E0",
                      fontWeight: "bold",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("createdAt")}
                  >
                    Date & Time
                    <span style={{ marginLeft: 6 }}>
                      {sortOrder.createdAt === "asc" ? "▲" : "▼"}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#FDF9E0",
                      fontWeight: "bold",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("fileName")}
                  >
                    File Name
                    <span style={{ marginLeft: 6 }}>
                      {sortOrder.fileName === "asc" ? "▲" : "▼"}
                    </span>
                  </TableCell>
                  <TableCell sx={{ color: "#FDF9E0", fontWeight: "bold" }}>
                    File URL
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#FDF9E0",
                      fontWeight: "bold",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("status")}
                  >
                    Status
                    <span style={{ marginLeft: 6 }}>
                      {sortOrder.status === "asc" ? "▲" : "▼"}
                    </span>
                  </TableCell>
                  <TableCell sx={{ color: "#FDF9E0", fontWeight: "bold" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={30} sx={{ color: "#FDF9E0" }} />
                    </TableCell>
                  </TableRow>
                ) : pendingFiles.length > 0 ? (
                  pendingFiles.map((file, index) => (
                    <TableRow key={file.fileUrl}>
                      <TableCell sx={{ color: "#FDF9E0" }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ color: "#FDF9E0" }}>
                        {formatISTDateTime(file.createdAt)}
                      </TableCell>
                      <TableCell sx={{ color: "#FDF9E0" }}>
                        {file.fileName}
                      </TableCell>
                      <TableCell sx={{ color: "#FDF9E0" }}>
                        {file.fileUrl ? (
                          <a
                            href={file.fileUrl}
                            target="_blank"                            
                            rel="noopener noreferrer"
                            style={{
                              color: "#FDF9E0",
                              textDecoration: "underline",
                            }}
                          >
                            View File
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#FDF9E0" }}>
                        {file.status || "Pending"}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {file.status === "Completed" && file.uuid ? (
                            <Button
                              variant="contained"
                              color="primary"
                              component="a"
                              target="_blank"
                              href={`/candidate-profile/${file.uuid}`}
                              sx={{
                                color: "#FDF9E0",
                                borderRadius: "8px",
                                textTransform: "none",
                              }}
                            >
                              View
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleProceed(file)}
                              disabled={loadingFiles[file.fileUrl]}
                              sx={{
                                "&.Mui-disabled": {
                                  backgroundColor: "#333",
                                  color: "#000",
                                },
                                color: "#FDF9E0",
                                borderRadius: "8px",
                                textTransform: "none",
                              }}
                            >
                              {loadingFiles[file.fileUrl]
                                ? "Processing..."
                                : "Process"}
                            </Button>
                          )}
                          <IconButton
                            aria-label="delete"
                            color="error"
                            onClick={() => handleDeleteClick(file)}
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ color: "#FDF9E0" }}>
                      No pending resumes found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "100%", xl: "70%" },
              bottom: 0,
              backgroundColor: "transparent",
              zIndex: 1,
            }}
          >
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                color: "#FDF9E0",
                "& .MuiTablePagination-select": { color: "#FDF9E0" },
                "& .MuiTablePagination-actions button": { color: "#FDF9E0" },
                "& svg": { fill: "#FDF9E0 !important" },
              }}
            />
          </Box>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={deleteLoading ? undefined : handleCancelDelete}
            aria-labelledby="delete-dialog-title"
            PaperProps={{
              sx: {
                backgroundColor: "#FDF9E0",
                borderRadius: 2,
                minWidth: 320,
              },
            }}
          >
            <DialogTitle
              id="delete-dialog-title"
              sx={{
                fontWeight: 700,
              }}
            >
              Confirm Delete
            </DialogTitle>
            <DialogContent>
              <Typography color="text.primary">
                Are you sure you want to delete this file?
              </Typography>
              <Typography
                color="error"
                sx={{
                  mt: 1,
                  fontWeight: 600,
                  wordBreak: "break-all",
                }}
              >
                {fileToDelete?.fileName}
              </Typography>
              {deleteLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={32} color="error" />
                </Box>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                pb: 2,
                pr: 3,
              }}
            >
              <Button
                onClick={handleCancelDelete}
                variant="outlined"
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
                disabled={deleteLoading}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default PendingResumes;
